/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";

export type WithdrawalProgressMode = "amount-first" | "wallet-first";

export function WithdrawalProgress({
  current,
  mode = "amount-first",
}: {
  current: 1 | 2 | 3;
  mode?: WithdrawalProgressMode;
}): VNode {
  const { i18n } = useTranslationContext();
  const amount = i18n.str`Choose amount`;
  const wallet = i18n.str`Open wallet`;
  const steps =
    mode === "amount-first"
      ? [amount, wallet, i18n.str`Review`]
      : [wallet, amount, i18n.str`Review`];

  return (
    <nav aria-label={i18n.str`Withdrawal progress`}>
      <ol class="grid grid-cols-3 text-xs sm:text-sm">
        {steps.map((label, index) => {
          const number = index + 1;
          const active = number === current;
          const complete = number < current;
          return (
            <li
              key={label}
              class="relative flex min-w-0 flex-col items-center px-1 text-center"
              aria-current={active ? "step" : undefined}
            >
              {index > 0 ? (
                <span
                  class={`absolute right-1/2 top-4 h-px w-full -translate-y-1/2 ${number <= current ? "bg-primary/60" : "bg-outlineVariant"}`}
                  aria-hidden="true"
                />
              ) : undefined}
              <span
                class={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold ${
                  active
                    ? "border-primary bg-primary text-onPrimary"
                    : complete
                      ? "border-brand bg-brand text-onBrand"
                      : "border-outlineVariant bg-white text-secondary"
                }`}
                aria-hidden="true"
              >
                {complete ? "✓" : number}
              </span>
              <span
                class={`mt-2 leading-5 ${
                  active
                    ? "font-semibold text-onBackground"
                    : complete
                      ? "font-medium text-onBackground"
                      : "text-secondary"
                }`}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
